import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import axiosInstance from "../../../services/AxiosInstance";
import { profileAction } from "../../../store/actions/AuthActions";
import "../../../css/rf.css";

const Tile = ({ icon, label, value, sub, to }) => (
  <Link to={to} className="rf-tile">
    <div className="rf-tile-top"><span className="rf-tile-ico">{icon}</span><span className="rf-label">{label}</span></div>
    <div className="rf-tile-val">{value}</div>
    <div className="rf-meta">{sub}</div>
  </Link>
);

const Card = ({ title, badge, children, action }) => (
  <div className="rf-card h-100">
    <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
      <b>{title}</b>{badge != null && <span className="rf-chip">{badge}</span>}
      <div className="rf-spacer" />{action}
    </div>
    {children}
  </div>
);

const Empty = ({ icon, text, to, cta }) => (
  <div className="rf-empty" style={{ padding: "22px 10px" }}>
    <div className="ico">{icon}</div><p className="mb-2">{text}</p>
    {to && <Link to={to} className="btn-rf-o d-inline-block">{cta}</Link>}
  </div>
);

const CAMPAIGN_STATUS = { 0: ["amber", "Pending"], 1: ["green", "Completed"], 2: ["red", "Cancelled"], 3: ["", "Paused"] };

const DashboardV2 = () => {
  const dispatch = useDispatch();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("dashboard/summary", { timeout: 25000 });
      if (data.status) { setD(data.data); setErr(""); } else setErr(data.message || "Could not load the dashboard");
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
  }, []);

  useEffect(() => {
    load();
    // same profile refresh the classic dashboard did, so the header keeps working
    axiosInstance.get("auth/profile").then(({ data }) => { if (data.profile) dispatch(profileAction(data.profile)); }).catch(() => {});
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load, dispatch]);

  if (!d && !err) return <div className="rf text-center py-5"><Spinner animation="border" variant="success" /></div>;

  if (!d) return (
    <div className="rf"><div className="rf-card rf-empty"><div className="ico">⚠️</div><h5>Dashboard could not load</h5><p>{err}</p>
      <button className="btn-rf" onClick={load}>Try again</button> <Link to="/dashboard-classic" className="btn-rf-o d-inline-block">Open classic dashboard</Link></div></div>
  );

  const { devices, messages, health, campaigns, autoReply, templates, channels, aiPosts, countries, upcoming } = d;
  const maxC = Math.max(1, ...countries.map((c) => c.count));
  const healthTotal = health.sent + health.failed + health.skipped;

  return (
    <div className="rf">
      <div className="rf-head">
        <div className="rf-head-icon">📊</div>
        <div><h3>Dashboard</h3><p>How your numbers, campaigns and channels are doing right now.</p></div>
        <div className="rf-spacer" />
        <Link to="/messages" className="btn-rf-o">Report</Link>
        <Link to="/send-message" className="btn-rf">Send Message</Link>
      </div>

      <div className="rf-tiles">
        <Tile icon="💬" label="Campaign messages" value={messages.total} sub={`${messages.deliveredPct}% delivered`} to="/messages" />
        <Tile icon="📱" label="Devices" value={`${devices.connected}/${devices.total}`} sub={`${devices.disconnected} disconnected`} to="/device" />
        <Tile icon="📣" label="Total campaigns" value={campaigns.total} sub={`${campaigns.active} active`} to="/messages" />
        <Tile icon="↩️" label="Auto reply" value={autoReply.rules} sub={`${autoReply.sent} messages sent`} to="/auto-reply" />
        <Tile icon="📢" label="Channels" value={channels.total} sub={`${channels.scheduled} posts scheduled`} to="/channel-manager" />
        <Tile icon="🗂" label="Templates" value={templates} sub="saved templates" to="/templates" />
      </div>

      <div className="row">
        <div className="col-lg-4 mb-3">
          <Card title="Contacts by country" badge={countries.length ? `${countries.length} countries` : null}>
            {countries.length === 0 ? <Empty icon="🌍" text="Numbers you message will show up here." to="/send-message" cta="Send a message" /> : countries.map((c) => (
              <div key={c.country} className="mb-2">
                <div className="d-flex justify-content-between" style={{ fontSize: 13 }}><span>{c.country}</span><b>{c.count}</b></div>
                <div className="rf-bar"><i style={{ width: `${(c.count / maxC) * 100}%` }} /></div>
              </div>
            ))}
          </Card>
        </div>

        <div className="col-lg-4 mb-3">
          <Card title="Queue overview" badge={`${messages.pending} in queue`}>
            <div className="rf-queue">
              <div><span className="rf-label">Pending</span><b>{messages.pending}</b></div>
              <div><span className="rf-label">Paused</span><b>{messages.paused}</b></div>
              <div><span className="rf-label">Message sent</span><b>{messages.sent}</b></div>
              <div><span className="rf-label">Message failed</span><b className={messages.failed ? "text-danger" : ""}>{messages.failed}</b></div>
            </div>
            {messages.paused > 0 && <div className="rf-help mt-2">Paused messages are waiting on a paused campaign.</div>}
          </Card>
        </div>

        <div className="col-lg-4 mb-3">
          <Card title="Devices" badge={`${devices.connected} online`} action={<Link to="/device" className="rf-meta">Manage</Link>}>
            {devices.list.length === 0 ? <Empty icon="📱" text="No device yet. Connect your WhatsApp to start." to="/device" cta="Connect a device" /> : devices.list.map((x) => (
              <div key={x.id} className="d-flex align-items-center py-1" style={{ gap: 8 }}>
                <span className={`rf-dot ${x.connected ? "on" : ""}`} /><span style={{ flex: 1 }}>{x.name || x.number || "Device"}</span>
                <span className="rf-meta">{x.connected ? "Connected" : "Offline"}</span>
              </div>
            ))}
          </Card>
        </div>

        <div className="col-lg-4 mb-3">
          <Card title="WhatsApp health" badge="last 7 days">
            {healthTotal === 0 ? <Empty icon="💚" text="No messages yet." /> : (
              <>
                <div className="rf-health">
                  <i style={{ width: `${(health.sent / healthTotal) * 100}%`, background: "var(--rf-green)" }} />
                  <i style={{ width: `${(health.failed / healthTotal) * 100}%`, background: "var(--rf-red)" }} />
                  <i style={{ width: `${(health.skipped / healthTotal) * 100}%`, background: "var(--rf-amber)" }} />
                </div>
                <div className="rf-queue mt-2">
                  <div><span className="rf-label">Sent</span><b>{health.sent}</b></div>
                  <div><span className="rf-label">Failed</span><b>{health.failed}</b></div>
                  <div><span className="rf-label">Skipped</span><b>{health.skipped}</b></div>
                </div>
              </>
            )}
          </Card>
        </div>

        <div className="col-lg-4 mb-3">
          <Card title="Campaigns" action={<Link to="/send-message" className="rf-meta">+ New</Link>}>
            {campaigns.recent.length === 0 ? <Empty icon="📣" text="No campaigns yet." to="/send-message" cta="Send Message" /> : campaigns.recent.map((c) => {
              const [cls, label] = CAMPAIGN_STATUS[c.status] || ["", "—"];
              return (
                <div key={c._id} className="d-flex align-items-center py-1" style={{ gap: 8 }}>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name || "Campaign"}</span>
                  <span className={`rf-chip ${cls}`}>{label}</span>
                </div>
              );
            })}
          </Card>
        </div>

        <div className="col-lg-4 mb-3">
          <Card title="Coming up" badge="next 7 days">
            {upcoming.length === 0 ? <Empty icon="🗓" text="Nothing scheduled this week." to="/channel-manager" cta="Schedule a post" /> : upcoming.map((u, i) => (
              <div key={i} className="d-flex py-1" style={{ gap: 8, fontSize: 13 }}>
                <span>{u.kind === "campaign" ? "📣" : "📢"}</span>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.title}{u.channel ? ` · ${u.channel}` : ""}</span>
                <span className="rf-meta">{format(new Date(u.at), "d MMM HH:mm")}</span>
              </div>
            ))}
          </Card>
        </div>

        <div className="col-lg-4 mb-3">
          <Card title="Sent automatically">
            <div className="rf-queue">
              <div><span className="rf-label">Auto replies sent</span><b>{autoReply.sent}</b></div>
              <div><span className="rf-label">AI channel posts</span><b>{aiPosts}</b></div>
            </div>
            <div className="rf-help mt-2">Replies and posts that went out without you pressing send.</div>
          </Card>
        </div>
      </div>

      <div className="text-center mt-2"><Link to="/dashboard-classic" className="rf-meta">Open classic dashboard</Link></div>
    </div>
  );
};

export default DashboardV2;
